/**
 * PATH: packages/email-hub/src/feature/components/charts/FlowEditor.tsx
 * FILE_ID: EMAILHUB-CHARTS-FLOWEDITOR-001
 * PURPOSE: Compact editor for flowchart nodes + edges. Nodes are labels
 *          (ids derived); edges pick from/to from the node list.
 * WHY: The manual entry path for flowcharts in FEAT-2026-07-03-COMPOSE-
 *      CHARTS. Emits a draft; the modal validates via parseFlowSpec so
 *      manual input obeys the same whitelist as AI output.
 * RELATED:
 *   - ComposeChartModal.tsx: owns the draft state this edits
 *   - chartRender.tsx: dagre auto-layout + React Flow preview
 */
import { Plus, X } from 'lucide-react'

export interface FlowDraft {
  nodes: string[]
  edges: Array<{ from: string; to: string; label: string }>
}

export function emptyFlowDraft(): FlowDraft {
  return {
    nodes: ['Start', 'Review', 'Done'],
    edges: [
      { from: 'Start', to: 'Review', label: '' },
      { from: 'Review', to: 'Done', label: 'approved' },
    ],
  }
}

const inputCls =
  'rounded border border-border bg-background px-1.5 py-1 text-xs text-foreground focus:border-primary focus:outline-none'

export function FlowEditor({
  draft,
  onChange,
}: {
  draft: FlowDraft
  onChange: (next: FlowDraft) => void
}) {
  const renameNode = (idx: number, name: string) => {
    const old = draft.nodes[idx]
    onChange({
      nodes: draft.nodes.map((n, i) => (i === idx ? name : n)),
      edges: draft.edges.map((e) => ({
        ...e,
        from: e.from === old ? name : e.from,
        to: e.to === old ? name : e.to,
      })),
    })
  }
  const addNode = () => {
    if (draft.nodes.length >= 40) return
    let n = draft.nodes.length + 1
    while (draft.nodes.includes(`Step ${n}`)) n++
    onChange({ ...draft, nodes: [...draft.nodes, `Step ${n}`] })
  }
  const removeNode = (idx: number) => {
    if (draft.nodes.length <= 2) return
    const name = draft.nodes[idx]
    onChange({
      nodes: draft.nodes.filter((_, i) => i !== idx),
      edges: draft.edges.filter((e) => e.from !== name && e.to !== name),
    })
  }
  const setEdge = (idx: number, patch: Partial<FlowDraft['edges'][number]>) => {
    onChange({
      ...draft,
      edges: draft.edges.map((e, i) => (i === idx ? { ...e, ...patch } : e)),
    })
  }
  const addEdge = () => {
    if (draft.edges.length >= 80 || draft.nodes.length < 2) return
    onChange({
      ...draft,
      edges: [...draft.edges, { from: draft.nodes[0], to: draft.nodes[1], label: '' }],
    })
  }
  const removeEdge = (idx: number) => {
    onChange({ ...draft, edges: draft.edges.filter((_, i) => i !== idx) })
  }

  return (
    <div className="flex flex-col gap-3" data-testid="flow-editor">
      <div>
        <div className="mb-1 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">Steps</div>
        <div className="flex flex-col gap-1">
          {draft.nodes.map((node, i) => (
            <div key={i} className="flex items-center gap-1">
              <input
                value={node}
                aria-label={`Step ${i + 1}`}
                onChange={(e) => renameNode(i, e.target.value)}
                className={`${inputCls} flex-1`}
              />
              {draft.nodes.length > 2 && (
                <button
                  type="button"
                  aria-label={`Remove step ${node}`}
                  onClick={() => removeNode(i)}
                  className="rounded p-0.5 text-muted-foreground hover:text-red-600"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </div>
          ))}
          <button
            type="button"
            onClick={addNode}
            className="inline-flex w-fit items-center gap-1 rounded border border-border px-2 py-1 text-xs text-muted-foreground hover:bg-accent"
          >
            <Plus className="h-3 w-3" /> Step
          </button>
        </div>
      </div>
      <div>
        <div className="mb-1 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">Connections</div>
        <div className="flex flex-col gap-1">
          {draft.edges.map((edge, i) => (
            <div key={i} className="flex items-center gap-1">
              <select
                value={edge.from}
                aria-label={`Connection ${i + 1} from`}
                onChange={(e) => setEdge(i, { from: e.target.value })}
                className={`${inputCls} min-w-0 flex-1`}
              >
                {draft.nodes.map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
              <span className="text-xs text-muted-foreground">→</span>
              <select
                value={edge.to}
                aria-label={`Connection ${i + 1} to`}
                onChange={(e) => setEdge(i, { to: e.target.value })}
                className={`${inputCls} min-w-0 flex-1`}
              >
                {draft.nodes.map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
              <input
                value={edge.label}
                placeholder="label"
                aria-label={`Connection ${i + 1} label`}
                onChange={(e) => setEdge(i, { label: e.target.value })}
                className={`${inputCls} w-20`}
              />
              <button
                type="button"
                aria-label={`Remove connection ${i + 1}`}
                onClick={() => removeEdge(i)}
                className="rounded p-0.5 text-muted-foreground hover:text-red-600"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={addEdge}
            className="inline-flex w-fit items-center gap-1 rounded border border-border px-2 py-1 text-xs text-muted-foreground hover:bg-accent"
          >
            <Plus className="h-3 w-3" /> Connection
          </button>
        </div>
      </div>
    </div>
  )
}
